import {
  ALL_REPAIR_RULES,
  type Diagnostic,
  type DiagnosticCode,
  EN_MESSAGES,
  type Fix,
  type FixConfidence,
  type LintReport,
  type MessageCatalog,
  type Params,
  type RepairRuleId,
  renderMessage,
  type Severity,
  type Span,
  type TextEdit,
} from '@jsonfmt/core';

/**
 * The problems surface, as data — docs/ARCHITECTURE.md §3 (`features/lint/problemList.ts`),
 * docs/DESIGN.md §6.4, ADR-0008.
 *
 * Everything in this file is PURE and DOM-free: it is compiled by `tsconfig.core.json`
 * (`lib: ["ES2022"]`, `types: []`) and tested in bare Node by vitest's `logic` project. The
 * three React files next to it render what this file computes and decide nothing.
 *
 * The one thing to understand before reading on: a `Diagnostic` carries NO `message`
 * (ADR-0008). The engine does not speak English. A row therefore needs a `MessageCatalog`
 * to exist at all, and the catalog arrives as data — from `useMessageCatalog()` in the UI,
 * from `EN_MESSAGES` in a test.
 */

// ── severity, in three independent channels ─────────────────────────────────────────────

/**
 * The glyph SHAPE for a severity (docs/DESIGN.md §6.4: "severity travels in three channels
 * — shape, word, tone"). Shape is first because it is the channel that survives a
 * greyscale print, a colour-blind reader and `forced-colors: active`.
 */
export type SeverityGlyph = 'dot' | 'triangle' | 'ring';

/** The CSS modifier suffix, matching the mockup's `.diag--err` / `--warn` / `--note`. */
export type SeverityVariant = 'err' | 'warn' | 'note';

const GLYPH: Readonly<Record<Severity, SeverityGlyph>> = {
  error: 'dot',
  warning: 'triangle',
  info: 'ring',
};

const VARIANT: Readonly<Record<Severity, SeverityVariant>> = {
  error: 'err',
  warning: 'warn',
  info: 'note',
};

/**
 * The WORD channel. A key rather than a string: `features/**` returns i18n keys and the
 * component translates (ADR-0018). The three keys exist in `locales/en/common.json` as
 * `Error` / `Warn` / `Note` — the mockup's three words, not the severity enum spelled out.
 */
const SEVERITY_LABEL_KEY: Readonly<Record<Severity, string>> = {
  error: 'editor.problems.severityError',
  warning: 'editor.problems.severityWarning',
  info: 'editor.problems.severityNote',
};

/** Sort rank. Exported because `statusModel.ts` and the tests both want the same order. */
export const SEVERITY_RANK: Readonly<Record<Severity, number>> = {
  error: 0,
  warning: 1,
  info: 2,
};

export function severityGlyph(severity: Severity): SeverityGlyph {
  return GLYPH[severity];
}

export function severityVariant(severity: Severity): SeverityVariant {
  return VARIANT[severity];
}

export function severityLabelKey(severity: Severity): string {
  return SEVERITY_LABEL_KEY[severity];
}

// ── ordering ────────────────────────────────────────────────────────────────────────────

/**
 * The engine already sorts and de-duplicates (ADR-0008). This repeats the same comparator
 * anyway, and that is deliberate rather than redundant:
 *
 *   · the panel must not depend on WHERE its diagnostics came from — a future Diff view, a
 *     concatenation of two reports and a hand-built fixture all have to produce the same
 *     twenty rows in the same order;
 *   · row identity (`ProblemRow.id`) is `code@from-to`, which is only unique once exact
 *     duplicates are collapsed. React keys are a correctness concern, not a lint warning.
 *
 * `previousProblem` / `nextProblem` walk this same list, so what the user sees on screen and
 * what F8 steps through cannot drift apart.
 */
export function orderDiagnostics(diagnostics: readonly Diagnostic[]): readonly Diagnostic[] {
  const sorted = [...diagnostics].sort((a, b) => {
    if (a.span.from !== b.span.from) return a.span.from - b.span.from;
    if (a.span.to !== b.span.to) return a.span.to - b.span.to;
    const rank = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (rank !== 0) return rank;
    return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
  });

  const seen = new Set<string>();
  const out: Diagnostic[] = [];
  for (const d of sorted) {
    const key = rowId(d);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(d);
  }
  return out;
}

/** Stable across re-renders of the same report; unique after {@link orderDiagnostics}. */
export function rowId(d: Diagnostic): string {
  return `${d.code}@${String(d.span.from)}-${String(d.span.to)}`;
}

// ── the message, split so the tokens inside it can be `<code>` ──────────────────────────

export interface MessageSegment {
  readonly kind: 'text' | 'code';
  readonly text: string;
}

/**
 * i18next interpolation, copied from `core/messages/en.ts` where it is private.
 * `messagePlaceholders()` is the exported half of that contract and `problemList.test.ts`
 * pins this regex against it for every template in the catalog, so the copy cannot drift.
 */
const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/**
 * Which interpolated parameters are a QUOTED FRAGMENT OF THE USER'S DOCUMENT, and therefore
 * get the `<code>` treatment of docs/DESIGN.md §6.4 ("inline tokens in the message are
 * `<code>` with a `--sunk` backing").
 *
 * This is a whitelist and not "every parameter" on purpose. Three of the catalog's
 * parameters are prose numbers (`depth`, `limit`, `reason`) and two are fragments of a
 * larger literal — `\{{ch}}` and `U+{{hex}}` — where a `<code>` box around the inner half
 * would cut a token in two on screen. Those stay text.
 */
const CODE_PARAMS: ReadonlySet<string> = new Set(['text', 'key', 'token', 'json', 'parsed']);

function templateFor(code: DiagnosticCode, catalog: MessageCatalog): string {
  const localized = catalog[code];
  if (typeof localized === 'string' && localized.length > 0) return localized;
  const english = EN_MESSAGES[code];
  return typeof english === 'string' && english.length > 0 ? english : code;
}

/**
 * The rendered sentence, split into runs of prose and runs of quoted document.
 *
 * Concatenating `segment.text` is byte-for-byte `renderMessage(code, catalog, params)` —
 * asserted for every code in the catalog. That equality is the whole safety argument: the
 * split happens on the TEMPLATE's placeholders, so nothing here parses prose, and a
 * translator reordering a sentence cannot make a token vanish.
 *
 * An unmatched placeholder is left verbatim, exactly as `renderMessage` leaves it — a
 * visible, greppable `{{text}}` beats a grammatical sentence that is quietly wrong.
 */
export function messageSegments(
  d: Pick<Diagnostic, 'code' | 'params'>,
  catalog: MessageCatalog,
): readonly MessageSegment[] {
  const template = templateFor(d.code, catalog);
  const params = d.params;
  const out: MessageSegment[] = [];

  const push = (kind: MessageSegment['kind'], text: string): void => {
    if (text.length === 0) return;
    const last = out[out.length - 1];
    if (last !== undefined && last.kind === 'text' && kind === 'text') {
      out[out.length - 1] = { kind: 'text', text: last.text + text };
      return;
    }
    out.push({ kind, text });
  };

  let cursor = 0;
  for (const match of template.matchAll(PLACEHOLDER)) {
    const name = match[1];
    const at = match.index;
    if (name === undefined) continue;
    const value = params?.[name];
    // Unmatched: fall through so the raw `{{name}}` ends up inside the surrounding text run.
    if (value === undefined) continue;

    push('text', template.slice(cursor, at));
    push(
      CODE_PARAMS.has(name) ? 'code' : 'text',
      typeof value === 'number' ? String(value) : value,
    );
    cursor = at + match[0].length;
  }
  push('text', template.slice(cursor));

  return out;
}

// ── the JSON path ───────────────────────────────────────────────────────────────────────

/**
 * `['upstreams', 1, 'circuitBreaker']` → `$.upstreams[1].circuitBreaker`.
 *
 * A key that is not a bare identifier is bracketed and JSON-quoted, so `$["a.b"]` is
 * unambiguous rather than a lie about the shape of the document.
 */
export function formatJsonPath(segments: readonly (string | number)[]): string {
  let out = '$';
  for (const segment of segments) {
    if (typeof segment === 'number') {
      out += `[${String(segment)}]`;
    } else if (/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(segment)) {
      out += `.${segment}`;
    } else {
      out += `[${JSON.stringify(segment)}]`;
    }
  }
  return out;
}

/**
 * Resolves the structural location of a span. The shell supplies
 * `(span) => tree === undefined ? undefined : pathAt(tree, span.from)` from the analysed
 * document; a caller that has only a `LintReport` (the Worker payload deliberately carries
 * no tree — ADR-0010) passes nothing and every row's `path` is `undefined`.
 */
export type PathResolver = (span: Span) => readonly (string | number)[] | undefined;

// ── the row ─────────────────────────────────────────────────────────────────────────────

export interface ProblemFix {
  readonly ruleId: RepairRuleId;
  readonly labelCode: `fix.${RepairRuleId}`;
  readonly labelParams: Params | undefined;
  /** The button's text, already rendered against the catalog. */
  readonly label: string;
  readonly confidence: FixConfidence;
  /** `false` ⇒ "Fix all" refuses it and only this single button can apply it (ADR-0012). */
  readonly safe: boolean;
  readonly edits: readonly TextEdit[];
}

export interface ProblemRow {
  /** `code@from-to`. Unique within a list, stable while the report is. */
  readonly id: string;
  /** Position in the FULL ordered list — the index `nextProblem` and the cursor speak in. */
  readonly index: number;
  readonly code: DiagnosticCode;
  readonly severity: Severity;
  readonly glyph: SeverityGlyph;
  readonly variant: SeverityVariant;
  /** i18n key for the severity WORD. */
  readonly severityKey: string;
  readonly message: readonly MessageSegment[];
  /** The same sentence flattened — for `title`, and for a test that wants one string. */
  readonly plainMessage: string;
  readonly span: Span;
  readonly line: number;
  readonly column: number;
  /** `$.upstreams[1].circuitBreaker`, or `undefined` when there is no tree to ask. */
  readonly path: string | undefined;
  readonly fix: ProblemFix | undefined;
}

export interface ProblemListOptions {
  readonly catalog: MessageCatalog;
  readonly pathOf?: PathResolver | undefined;
  /** `'errors'` is the `⌥E` filter of docs/DESIGN.md §7.4. */
  readonly filter?: 'all' | 'errors' | undefined;
}

function toFix(fix: Fix, catalog: MessageCatalog): ProblemFix {
  return {
    ruleId: fix.ruleId,
    labelCode: fix.labelCode,
    labelParams: fix.labelParams,
    label: renderMessage(fix.labelCode, catalog, fix.labelParams),
    confidence: fix.confidence,
    safe: fix.confidence === 'safe',
    edits: fix.edits,
  };
}

/**
 * `Diagnostic[]` → `ProblemRow[]`.
 *
 * `index` is the position in the UNFILTERED list. That matters: `⌥E` hides the warnings but
 * must not renumber the errors underneath the cursor, or F8 would jump somewhere else the
 * moment the filter is toggled.
 */
export function buildProblemList(
  diagnostics: readonly Diagnostic[],
  options: ProblemListOptions,
): readonly ProblemRow[] {
  const { catalog, pathOf, filter = 'all' } = options;
  const ordered = orderDiagnostics(diagnostics);
  const rows: ProblemRow[] = [];

  for (let index = 0; index < ordered.length; index += 1) {
    const d = ordered[index];
    if (d === undefined) continue;
    if (filter === 'errors' && d.severity !== 'error') continue;

    const segments = messageSegments(d, catalog);
    const path = pathOf?.(d.span);

    rows.push({
      id: rowId(d),
      index,
      code: d.code,
      severity: d.severity,
      glyph: severityGlyph(d.severity),
      variant: severityVariant(d.severity),
      severityKey: severityLabelKey(d.severity),
      message: segments,
      plainMessage: segments.map((s) => s.text).join(''),
      span: d.span,
      line: d.start.line,
      column: d.start.column,
      path: path === undefined || path.length === 0 ? undefined : formatJsonPath(path),
      fix: d.fix === undefined ? undefined : toFix(d.fix, catalog),
    });
  }

  return rows;
}

/** True when at least one row carries a fix — the panel reserves its fix gutter on that. */
export function hasFixableRow(rows: readonly ProblemRow[]): boolean {
  return rows.some((row) => row.fix !== undefined);
}

// ── which fixes "Fix all" is allowed to touch ───────────────────────────────────────────

/**
 * The `safe` half of the rule registry, derived from `ALL_REPAIR_RULES` rather than retyped.
 *
 * Retyping it is exactly the defect ADR-0012 exists to prevent: a nineteenth rule declared
 * `heuristic` in `core/repair/registry.ts` and forgotten in a hand-written list here would
 * be applied silently in bulk by "Fix all" — which is how the legacy fixer turned
 * `{"a":NaN}` into `{"a":"NaN"}`. Derived, this cannot happen: a new rule is classified once,
 * by its own declaration.
 */
const SAFE_RULE_IDS: ReadonlySet<RepairRuleId> = new Set(
  ALL_REPAIR_RULES.filter((rule) => rule.confidence === 'safe').map((rule) => rule.id),
);

export function isSafeRuleId(id: RepairRuleId): boolean {
  return SAFE_RULE_IDS.has(id);
}

/**
 * The diagnostics "Fix all" would close. `confidence === 'safe'` AND the rule itself declared
 * `safe` — two independent readings of the same fact, because the whole safety contract of
 * ADR-0012 rests on this predicate and a `Fix` is a structure that crossed a Worker boundary.
 */
export function safeFixes(diagnostics: readonly Diagnostic[]): readonly Diagnostic[] {
  return diagnostics.filter(
    (d) => d.fix !== undefined && d.fix.confidence === 'safe' && isSafeRuleId(d.fix.ruleId),
  );
}

/**
 * NOT `report.counts.fixable`.
 *
 * `counts.fixable` counts every attached `Fix`, heuristic ones included. Lighting the
 * "Fix all" button from it would offer the action on a document where pressing it changes
 * nothing — `{"a":NaN}` has one fixable diagnostic and zero safe fixes.
 */
export function safeFixableCount(report: LintReport | null): number {
  return report === null ? 0 : safeFixes(report.diagnostics).length;
}

// ── the cursor ──────────────────────────────────────────────────────────────────────────

/**
 * WHICH ROW IS CURRENT — the one piece of mutable state this feature owns, and the one
 * deliberate dent in "commands are data" (ADR-0014).
 *
 * The reason it has to exist here rather than inside `ProblemsPanel`: `F8` is a global
 * command, `aria-current="true"` is a panel concern, and the editor's caret is behind
 * `UiPort`, which has no getter. Three consumers, one index. A component-local `useState`
 * would let F8 and the panel disagree about "the current problem" — which is the one thing
 * a problems list may never do.
 *
 * It is a plain observable read through `useSyncExternalStore`, like `toastHub`: no React,
 * no DOM, testable in bare Node, and injectable so `commands.test.ts` never touches the
 * module-level instance.
 */
export interface ProblemCursor {
  subscribe(listener: () => void): () => void;
  /** `-1` when nothing is selected. */
  getSnapshot(): number;
  select(index: number): void;
  clear(): void;
}

export function createProblemCursor(initial = -1): ProblemCursor {
  let index = initial;
  const listeners = new Set<() => void>();

  const emit = (): void => {
    for (const listener of [...listeners]) listener();
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      return index;
    },
    select(next) {
      const clamped = Number.isInteger(next) && next >= 0 ? next : -1;
      if (clamped === index) return;
      index = clamped;
      emit();
    },
    clear() {
      if (index === -1) return;
      index = -1;
      emit();
    },
  };
}

/** The instance the shipped commands and the shipped panel share. */
export const problemCursor: ProblemCursor = createProblemCursor();

/**
 * Where `↑` / `↓` / `F8` land.
 *
 * Wraps in both directions, and treats "nothing selected" as "one before the first" so the
 * first `F8` on a fresh report goes to row 0 rather than to row 1. Returns `-1` for an empty
 * list, which every caller reads as "there is nothing to jump to".
 */
export function stepIndex(current: number, delta: number, count: number): number {
  if (count <= 0) return -1;
  if (current < 0 || current >= count) return delta >= 0 ? 0 : count - 1;
  return (((current + delta) % count) + count) % count;
}

/** Keeps a stored index meaningful after the report shrank underneath it. */
export function clampIndex(index: number, count: number): number {
  if (count <= 0) return -1;
  if (index < 0) return -1;
  return index >= count ? count - 1 : index;
}
